/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
/** HTTP-клиент: токен доступа в памяти, refresh-токен в localStorage, автоматическое продление сессии. */
const BASE = '/api/v1';
const RT_KEY = 'kd.rt';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onLogout: () => void = () => {};

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: any) { super(message); }
}

const store = {
  get: () => { try { return localStorage.getItem(RT_KEY); } catch { return null; } },
  set: (v: string | null) => { try { v ? localStorage.setItem(RT_KEY, v) : localStorage.removeItem(RT_KEY); } catch { /* приватный режим */ } },
};

export function setSession(t: { accessToken: string; refreshToken: string } | null) {
  accessToken = t?.accessToken ?? null;
  store.set(t?.refreshToken ?? null);
}
export function hasRefresh() { return !!store.get(); }
export function setLogoutHandler(fn: () => void) { onLogout = fn; }
export function getAccessToken() { return accessToken; }

async function refresh(): Promise<boolean> {
  const rt = store.get();
  if (!rt) return false;
  refreshing ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) })
    .then(async (r) => {
      if (!r.ok) { setSession(null); return false; }
      setSession(await r.json());
      return true;
    })
    .catch(() => false)
    .finally(() => { refreshing = null; });
  return refreshing;
}

export async function request<T = any>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  if (!accessToken && hasRefresh() && !path.startsWith('/auth/login')) await refresh();
  const isForm = body instanceof FormData;
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { ...(isForm || body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  if (r.status === 401 && retry && !path.startsWith('/auth/')) {
    if (await refresh()) return request<T>(method, path, body, false);
    onLogout();
  }
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get('content-type') ?? '';
  const data = ct.includes('json') ? await r.json() : await r.text();
  if (!r.ok) {
    const msg = typeof data === 'object' && data?.message ? (Array.isArray(data.message) ? data.message.join('; ') : data.message) : `Ошибка ${r.status}`;
    const details = typeof data === 'object' && data?.errors ? ': ' + data.errors.map((e: any) => `${e.path} - ${e.message}`).join('; ') : '';
    throw new ApiError(r.status, msg + details, data);
  }
  return data as T;
}

export const api = {
  get: <T = any>(p: string) => request<T>('GET', p),
  post: <T = any>(p: string, b?: unknown) => request<T>('POST', p, b ?? {}),
  patch: <T = any>(p: string, b: unknown) => request<T>('PATCH', p, b),
  put: <T = any>(p: string, b: unknown) => request<T>('PUT', p, b),
  del: <T = any>(p: string) => request<T>('DELETE', p),
};

/** Скачивание файла с авторизацией (имя берётся из Content-Disposition). */
export async function download(path: string) {
  if (!accessToken) await refresh();
  const r = await fetch(`${BASE}${path}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  if (!r.ok) throw new ApiError(r.status, 'Не удалось скачать файл');
  const cd = r.headers.get('content-disposition') ?? '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const name = m ? decodeURIComponent(m[1]) : 'file';
  const url = URL.createObjectURL(await r.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function qs(params: Record<string, unknown>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
}
